import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    DataGridPro,
    GridColDef,
    GridFilterModel,
    GridPagination,
    GridSortItem,
    GridSortModel,
    GridToolbarColumnsButton,
    GridToolbarContainer,
    GridToolbarDensitySelector,
    GridToolbarFilterButton
} from '@mui/x-data-grid-pro';
import { Box, Button, CircularProgress, FormControlLabel, Switch } from '@mui/material';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../context/store';
import { RootState } from '../../context/rootReducer';
import { getTasklistsAsync, downloadTasklistsCSVAsync } from '../../slices/tasklists/tasklistsSlice';
import {
    tasklistColumns,
    customDateColumnType,
    quickFiltersSortOrder,
    getTasklistStatusColor,
    buttonBaseProps,
    TASKLIST_SORT_KEY_BY_FIELD
} from './TasklistsProps';
import {
    SIZE_PER_PAGE_LIST,
    START_PAGE,
    DEFAULT_TABLE_SORTING_ORDER,
    DATA_GRID_COMMON_COMPONENTS_PROPS_STYLES,
    DATA_GRID_COMMON_SX_STYLES,
    RP_BLUE,
    FILTER_TIP_ICON_SIZE,
    DEFAULT_TABLE_SORT_MODEL,
    TASKLISTS_QUICK_FILTERS_TIP
} from '../../lib/constants';
import { addDateToFileName, formatTimeAgoVerbose } from '../../lib/helpers/helpers';
import {
    TasklistFilters,
    TasklistRow,
    TasklistSortBy,
    TasklistSortDir,
    TasklistSortOrder,
    TasklistStatus
} from '../../lib/types';
import {
    TASKLIST_TEXT_FILTERABLE_FIELDS,
    TasklistsPropKey,
    TasklistsPropMap,
    TextFilterField
} from '../../lib/dictionaries/TasklistsDictionary';

const UI_TO_API_FILTER_KEY: Record<TextFilterField, keyof TasklistFilters> = {
    [TasklistsPropMap.largeScaleEventName]: 'lseName',
    [TasklistsPropMap.eventName]: 'eventName',
    [TasklistsPropMap.title]: 'title',
    [TasklistsPropMap.country]: 'country',
    [TasklistsPropMap.state]: 'state',
    [TasklistsPropMap.city]: 'city',
    [TasklistsPropMap.siteCode]: 'siteCode',
    [TasklistsPropMap.gsoCluster]: 'gsoCluster',
    [TasklistsPropMap.reasons]: 'reasons'
};
const TEXT_FILTERABLE_FIELDS = new Set<string>(TASKLIST_TEXT_FILTERABLE_FIELDS);
const ALL_STATUSES: TasklistStatus[] = ['Activated', 'In Progress', 'Completed', 'Deactivated'];

const TasklistsDataGrid: React.FC = () => {
    const dispatch = useAppDispatch();
    const gridRootRef = useRef<HTMLDivElement | null>(null);

    const {
        tasklists = [],
        totalCount = 0,
        isGettingTasklists = false,
        statusCounts = null,
        lastDataRefreshAt
    } = useSelector((state: RootState) => state.tasklists || {});

    const ZERO_COUNTS: Record<TasklistStatus, number> = {
        Activated: 0,
        'In Progress': 0,
        Completed: 0,
        Deactivated: 0
    };

    const countsForPills = useMemo(() => {
        const isEmpty = !statusCounts || Object.keys(statusCounts).length === 0;
        return isEmpty ? ZERO_COUNTS : (statusCounts as Record<TasklistStatus, number>);
    }, [statusCounts]);

    const [pageNumber, setPageNumber] = useState(START_PAGE);
    const [pageSize, setPageSize] = useState(50);
    const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
    const [sortModel, setSortModel] = useState<GridSortModel>([DEFAULT_TABLE_SORT_MODEL as GridSortItem]);
    const [statusFilter, setStatusFilter] = useState<TasklistStatus[]>([]);
    const [autoReload, setAutoReload] = useState(false);

    const columnTypes = useMemo(() => ({ customDate: customDateColumnType }), []);

    const isFilterModelComplete = useCallback((model: GridFilterModel) => {
        return model.items.every((item) => {
            if (!TEXT_FILTERABLE_FIELDS.has(item.columnField)) {
                return true;
            }
            return item.value != null && String(item.value).trim() !== '';
        });
    }, []);

    const buildFilters = useCallback((): TasklistFilters => {
        const filters: TasklistFilters = {};
        if (statusFilter.length > 0) {
            filters.statusIn = statusFilter;
        }

        filterModel.items.forEach(({ columnField, value }) => {
            if (!value || !TEXT_FILTERABLE_FIELDS.has(columnField)) {
                return;
            }
            const apiKey = UI_TO_API_FILTER_KEY[columnField as TextFilterField];
            const curr = (filters as any)[apiKey];
            (filters as any)[apiKey] = curr
                ? Array.isArray(curr)
                    ? [...curr, value]
                    : [curr, value]
                : [value];
        });
        return filters;
    }, [filterModel, statusFilter]);

    const buildSort = useCallback((): TasklistSortOrder => {
        const s = sortModel[0];
        const field = (s?.field ?? TasklistsPropMap.start) as TasklistsPropKey;
        const column =
            (TASKLIST_SORT_KEY_BY_FIELD as Record<TasklistsPropKey, TasklistSortBy>)[field] ?? 'start';
        const order = (s?.sort ?? 'desc') as TasklistSortDir;
        return { column, order };
    }, [sortModel]);

    const tableFilterAndSortHandler = useCallback(() => {
        if (!isFilterModelComplete(filterModel)) {
            return;
        }
        const apiFilters = buildFilters();
        const apiSort = buildSort();
        dispatch(getTasklistsAsync(pageNumber, pageSize, apiFilters, apiSort));
    }, [dispatch, pageNumber, pageSize, buildFilters, buildSort, filterModel, isFilterModelComplete]);

    // Global input routing for horizontal navigation:
    // - Shift + Mouse Wheel or left and right trackpad swiping anywhere scrolls the DataGrid horizontally.
    // - Left and right arrows scroll the grid even when focus is not inside the grid.
    // - Clamps to grid bounds, respects typing fields and OS/browser shortcuts,
    //   and only preventDefault() when we actually moved the grid.
    useEffect(() => {
        const getScroller = () => {
            const root = gridRootRef.current;
            return root?.querySelector('.MuiDataGrid-virtualScroller') as HTMLElement | null;
        };

        const clampScroll = (scroller: HTMLElement, next: number) => {
            const maxLeft = scroller.scrollWidth - scroller.clientWidth;
            scroller.scrollLeft = Math.max(0, Math.min(next, maxLeft));
        };

        const isTyping = (element: Element | null) => {
            if (!element) {
                return false;
            }
            const tag = (element as HTMLElement).tagName?.toLowerCase();
            return tag === 'input' || tag === 'textarea' || (element as HTMLElement).isContentEditable;
        };

        const onWheel = (e: WheelEvent) => {
            if (e.ctrlKey) {
                return;
            }

            const dx = e.deltaX || (e.shiftKey ? e.deltaY : 0);
            if (!dx) {
                return;
            }

            const scroller = getScroller();
            if (!scroller || scroller.scrollWidth <= scroller.clientWidth) {
                return;
            }

            const prev = scroller.scrollLeft;
            clampScroll(scroller, prev + dx);

            if (scroller.scrollLeft !== prev) {
                e.preventDefault();
                e.stopPropagation();
            }
        };

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') {
                return;
            }
            if (e.altKey || e.metaKey || e.ctrlKey) {
                return;
            }
            if (isTyping(document.activeElement)) {
                return;
            }

            const root = gridRootRef.current;
            if (root && document.activeElement && root.contains(document.activeElement)) {
                return;
            }

            const scroller = getScroller();
            if (!scroller || scroller.scrollWidth <= scroller.clientWidth) {
                return;
            }

            const STEP = 100;
            const delta = e.key === 'ArrowLeft' ? -STEP : STEP;

            const prev = scroller.scrollLeft;
            clampScroll(scroller, prev + delta);

            if (scroller.scrollLeft !== prev) {
                e.preventDefault();
                e.stopPropagation();
            }
        };

        window.addEventListener('wheel', onWheel, { passive: false, capture: true });
        window.addEventListener('keydown', onKeyDown, { capture: true });

        return () => {
            window.removeEventListener('wheel', onWheel, { capture: true });
            window.removeEventListener('keydown', onKeyDown, { capture: true });
        };
    }, []);

    useEffect(() => {
        tableFilterAndSortHandler();
        if (!autoReload) {
            return;
        }
        const id = window.setInterval(tableFilterAndSortHandler, 30000);
        return () => window.clearInterval(id);
    }, [autoReload, tableFilterAndSortHandler]);

    const handleFilterChange = useCallback((newFilterModel: GridFilterModel) => {
        setPageNumber(START_PAGE);
        setFilterModel(newFilterModel);
    }, []);

    const handleStatusFilterClick = useCallback(
        (status: TasklistStatus) => {
            setPageNumber(START_PAGE);

            setStatusFilter((prev) => {
                const deselecting = prev.includes(status);
                const next = deselecting ? prev.filter((s) => s !== status) : [...prev, status];

                if (next.length === 1) {
                    const m = quickFiltersSortOrder[next[0]];
                    if (m) setSortModel([m as GridSortItem]);
                } else {
                    setSortModel([DEFAULT_TABLE_SORT_MODEL as GridSortItem]);
                }

                return next;
            });
        },
        [setPageNumber, setStatusFilter, setSortModel]
    );

    const clearAllFilters = useCallback(() => {
        setPageNumber(START_PAGE);
        setFilterModel({ items: [] });
        setStatusFilter([]);
        setSortModel([DEFAULT_TABLE_SORT_MODEL as GridSortItem]);
    }, []);

    const onPageChangeHandler = (newPage: number) => {
        setPageNumber(newPage + START_PAGE);
    };

    const onPageSizeChangeHandler = (newPageSize: number) => {
        setPageNumber(START_PAGE);
        setPageSize(newPageSize);
    };

    const defaultExportSort: TasklistSortOrder = useMemo(() => {
        const df = DEFAULT_TABLE_SORT_MODEL as GridSortItem;
        const field = df.field as TasklistsPropKey;
        const column =
            (TASKLIST_SORT_KEY_BY_FIELD as Record<TasklistsPropKey, TasklistSortBy>)[field] ?? 'start';
        const order = (df.sort ?? 'desc') as TasklistSortDir;
        return { column, order };
    }, []);

    const CustomToolbar = (props: any): JSX.Element => {
        return (
            <Box>
                <StatusToggleBar
                    onStatusClick={handleStatusFilterClick}
                    currentFilter={statusFilter}
                    statusCounts={countsForPills as Record<TasklistStatus, number>}
                    isCountsLoading={isGettingTasklists}
                />
                <GridToolbarContainer>
                    <GridToolbarFilterButton {...props} />
                    <GridToolbarDensitySelector {...props} />
                    <GridToolbarColumnsButton {...props} />

                    <Button
                        {...buttonBaseProps}
                        onClick={() =>
                            dispatch(
                                downloadTasklistsCSVAsync(
                                    addDateToFileName('Tasklists.csv'),
                                    null,
                                    defaultExportSort
                                )
                            )
                        }
                    >
                        Full Export
                    </Button>

                    <Button
                        {...buttonBaseProps}
                        onClick={() =>
                            dispatch(
                                downloadTasklistsCSVAsync(
                                    addDateToFileName('Filtered-Tasklists.csv'),
                                    buildFilters(),
                                    buildSort()
                                )
                            )
                        }
                    >
                        Filtered Export
                    </Button>

                    <Button onClick={clearAllFilters} size='small'>
                        Clear Filters
                    </Button>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: 1 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    sx={{ transform: 'translateY(2px)' }}
                                    checked={autoReload}
                                    onChange={(e) => setAutoReload(e.target.checked)}
                                />
                            }
                            label='Auto Reload'
                            componentsProps={{
                                typography: {
                                    sx: {
                                        color: RP_BLUE,
                                        fontSize: '13px',
                                        fontFamily: 'Inter',
                                        paddingTop: '8px'
                                    }
                                }
                            }}
                            labelPlacement='start'
                        />

                        <Box sx={{ fontSize: '13px', fontFamily: 'Inter', color: '#222' }}>
                            {lastDataRefreshAt
                                ? `Last updated ${formatTimeAgoVerbose(lastDataRefreshAt)}`
                                : ''}
                        </Box>
                    </Box>
                </GridToolbarContainer>
            </Box>
        );
    };

    return (
        <div style={{ width: '100%' }} ref={gridRootRef}>
            <DataGridPro
                rows={tasklists}
                getRowId={(row) => row.tasklistId}
                columns={tasklistColumns as GridColDef<TasklistRow>[]}
                rowCount={totalCount}
                loading={isGettingTasklists}
                pagination
                filterMode='server'
                sortingMode='server'
                paginationMode='server'
                page={pageNumber - START_PAGE}
                pageSize={pageSize}
                filterModel={filterModel}
                sortModel={sortModel}
                onPageChange={onPageChangeHandler}
                onPageSizeChange={onPageSizeChangeHandler}
                onFilterModelChange={handleFilterChange}
                onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
                autoHeight
                columnTypes={columnTypes}
                rowsPerPageOptions={SIZE_PER_PAGE_LIST}
                sortingOrder={DEFAULT_TABLE_SORTING_ORDER as any}
                components={{
                    Toolbar: CustomToolbar,
                    Pagination: GridPagination,
                    LoadingOverlay: () => (
                        <Box
                            sx={{
                                width: '100%',
                                py: 4,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    )
                }}
                componentsProps={{
                    ...DATA_GRID_COMMON_COMPONENTS_PROPS_STYLES,
                    filterPanel: {
                        sx: {
                            '& .MuiDataGrid-filterFormLinkOperatorInput': { display: 'none' },
                            '& .MuiDataGrid-filterForm': { minWidth: '600px' },
                            '& .MuiTextField-root': { minWidth: '125px' }
                        }
                    }
                }}
                disableColumnMenu={true}
                disableSelectionOnClick
                sx={DATA_GRID_COMMON_SX_STYLES}
            />
        </div>
    );
};

const StatusToggleBar: React.FC<{
    onStatusClick: (status: TasklistStatus) => void;
    currentFilter: TasklistStatus[];
    statusCounts: Partial<Record<TasklistStatus, number>>;
    isCountsLoading: boolean;
}> = ({ onStatusClick, currentFilter, statusCounts, isCountsLoading }) => {
    const [isTextVisible, setIsTextVisible] = useState(false);

    return (
        <div style={{ position: 'relative' }}>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    marginBottom: 10,
                    gap: '8px'
                }}
            >
                {ALL_STATUSES.map((status) => {
                    const isActive = currentFilter.includes(status);
                    const color = getTasklistStatusColor(status);
                    const count = statusCounts?.[status] ?? 0;

                    return (
                        <div
                            key={status}
                            onClick={() => onStatusClick(status)}
                            style={{
                                cursor: 'pointer',
                                userSelect: 'none',
                                padding: '6px 12px',
                                borderRadius: 4,
                                fontWeight: 'bold',
                                backgroundColor: isActive ? 'transparent' : color,
                                color: isActive ? color : '#fff',
                                border: `2px solid ${color}`,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}
                        >
                            {status}
                            {isCountsLoading ? <CircularProgress size={14} color='inherit' /> : `(${count})`}
                        </div>
                    );
                })}

                <AiOutlineInfoCircle
                    onClick={() => setIsTextVisible(!isTextVisible)}
                    size={FILTER_TIP_ICON_SIZE}
                    color={RP_BLUE}
                    style={{ cursor: 'pointer' }}
                />
                {isTextVisible && <i className='info-text'>{TASKLISTS_QUICK_FILTERS_TIP}</i>}
            </div>
        </div>
    );
};

export default TasklistsDataGrid;
